import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Doc, Id } from '../../../../convex/_generated/dataModel';
import { TagChip } from './TagChip';
import { SimpleTagChip } from './SimpleTagChip';
import { useSearch } from '../../contexts/SearchContext';
import { useFullscreenViewer } from '../../contexts/FullscreenViewerContext';
import type { ViewerMediaItem } from '../../contexts/FullscreenViewerContext';

type ReferenceDoc = Doc<'references'> & {
  imageUrl?: string | null;
  sourceURL?: string | null;
  tags: Doc<'tags'>[];
  mediaItems: (Doc<'mediaItems'> & {
    url: string | null;
    mediaType: 'image' | 'video';
  })[];
  app?: Doc<'apps'> | null;
};

interface ReferenceCardProps {
  reference: ReferenceDoc;
  onEdit?: (reference: ReferenceDoc) => void;
  onDelete?: (reference: ReferenceDoc) => void;
  onAIPrompt?: (reference: ReferenceDoc) => void;
  onTagClick?: (tagId: string) => void;
  generateReferenceUrl?: (referenceId: string) => string;
  viewMode?: 'grid' | 'list' | 'masonry';
  onCopyImage?: () => Promise<boolean>;
  onCopyLink?: () => Promise<boolean>;
  onRemove?: (referenceId: Id<'references'>) => void;
  showSelection?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (referenceId: Id<'references'>) => void;
}

export function ReferenceCard({
  reference: ref,
  onEdit,
  onDelete,
  onAIPrompt,
  onTagClick,
  generateReferenceUrl,
  viewMode = 'grid',
  onCopyImage,
  onCopyLink,
  onRemove,
  showSelection,
  isSelected,
  onToggleSelection
}: ReferenceCardProps) {
  const navigate = useNavigate();
  const { filters, filterState, filterDispatch, hasActiveFilters: hasNewFilters } = useSearch();
  const { openViewer } = useFullscreenViewer();

  // Default URL generator if none provided
  const defaultGenerateUrl = (referenceId: string) => {
    const currentParams = new URLSearchParams(window.location.search);
    const queryString = currentParams.toString();
    return `/reference/${referenceId}${queryString ? `?${queryString}` : ''}`;
  };

  const urlGenerator = generateReferenceUrl || defaultGenerateUrl;

  // Helper function to safely extract hostname from a URL
  const getHostnameFromUrl = (url: string): string | null => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      try {
        const cleanUrl = url.replace(/^https?:\/\//, '');
        const domain = cleanUrl.split(/[/?#]/)[0];
        if (domain && domain.includes('.')) {
          return domain;
        }
      } catch {
        // If all fails, return null
      }
      return null;
    }
  };

  const handleTagClick = (tagId: string) => {
    if (onTagClick) {
      onTagClick(tagId);
    }
  };

  const handleCardClick = () => {
    void navigate(urlGenerator(ref._id));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void handleCardClick();
    }
  };

  // Copy Icon Component
  const CopyIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );

  // Share Icon Component
  const ShareIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
    </svg>
  );

  // Grid view (default)
  return (
    <div className="card bg-yellow-300 shadow-lg border-2 border-gray-800 hover:shadow-xl transition-shadow flex flex-col overflow-hidden relative group">
      {/* Selection Checkbox - Top Left */}
      {showSelection && onToggleSelection && (
        <div className="absolute top-2 left-2 z-20">
          <label className="cursor-pointer">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelection(ref._id)}
              className="checkbox checkbox-md border-2 border-gray-800 bg-white hover:bg-white checked:bg-yellow-400 checked:border-gray-800 focus-visible:outline-yellow-400 focus-visible:outline-offset-2 focus-visible:outline-2"
              onClick={(e) => e.stopPropagation()}
              style={{
                '--chkbg': isSelected ? '#facc15' : 'white',
                '--chkfg': isSelected ? '#1f2937' : 'transparent',
              } as React.CSSProperties}
            />
          </label>
        </div>
      )}

      {/* Copy, Share, and Remove Buttons - Top Right */}
      {(onCopyImage || onCopyLink || onRemove) && (
        <div className="absolute top-2 right-2 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {onCopyImage && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                void onCopyImage();
              }}
              className="btn btn-sm btn-circle bg-gray-800/90 hover:bg-gray-900 border-gray-700 text-white hover:text-white"
              title="Copy image"
              aria-label="Copy image to clipboard"
            >
              <CopyIcon className="w-3 h-3" />
            </button>
          )}
          {onCopyLink && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                void onCopyLink();
              }}
              className="btn btn-sm btn-circle bg-gray-800/90 hover:bg-gray-900 border-gray-700 text-white hover:text-white"
              title="Copy link"
              aria-label="Copy reference link"
            >
              <ShareIcon className="w-3 h-3" />
            </button>
          )}
          {onRemove && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove(ref._id);
              }}
              className="btn btn-sm btn-circle bg-red-500/90 hover:bg-red-600 border-red-600 text-white hover:text-white"
              title="Remove from project"
              aria-label="Remove reference from project"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Image/Video Thumbnail */}
      <div
        className="cursor-pointer"
        onClick={handleCardClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`View reference: ${ref.title}`}
      >
        <div className="aspect-video bg-gray-100 relative">
          {ref.mediaItems?.[0]?.url ? (
            ref.mediaItems[0].mediaType === 'video' ? (
              <div className="relative w-full h-full">
                <video
                  src={ref.mediaItems[0].url}
                  className="w-full h-full object-cover"
                  muted
                  preload="metadata"
                  poster=""
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                  <div className="bg-white bg-opacity-90 rounded-full p-2 sm:p-3">
                    <svg className="w-6 sm:w-8 h-6 sm:h-8 text-gray-800" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <title>Play video</title>
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
              </div>
            ) : (
              <img
                src={ref.mediaItems[0].url}
                alt={ref.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )
          ) : ref.imageUrl ? (
            <img
              src={ref.imageUrl}
              alt={ref.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <svg className="w-8 sm:w-12 h-8 sm:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <title>No media</title>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          {(ref.mediaItems?.[0]?.url || ref.imageUrl) && (
            <button
              className="absolute bottom-2 right-2 z-10 p-1.5 rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70 sm:opacity-0 max-sm:opacity-70"
              onClick={(e) => {
                e.stopPropagation();
                const items: ViewerMediaItem[] = ref.mediaItems
                  ?.filter((item): item is typeof item & { url: string } => item.url !== null)
                  .map((item) => ({ url: item.url, mediaType: item.mediaType })) ?? [];
                if (items.length === 0 && ref.imageUrl) {
                  items.push({ url: ref.imageUrl, mediaType: 'image' as const });
                }
                openViewer(items, 0);
              }}
              aria-label="View fullscreen"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="card-body p-3 sm:p-4 flex-grow flex flex-col">
        <div className="flex-grow">
          <h3 className="card-title text-gray-900 mb-2 line-clamp-2 text-sm sm:text-base">{ref.title}</h3>
          {ref.description && (
            <p className="text-xs sm:text-sm text-gray-700 mb-2 sm:mb-3 line-clamp-3">{ref.description}</p>
          )}

          {/* Tags */}
          {ref.tags && ref.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {ref.tags.slice(0, 3).map((tag) => {
                const isActiveInNewSystem = Object.values(filterState.tags).some(filterTag => filterTag.value === tag.name);
                const activeTagMode = Object.values(filterState.tags).find(filterTag => filterTag.value === tag.name)?.mode;
                const isActiveInLegacy = filters.tags.some((filterTag: { id: Id<'tags'>; name: string; negative?: boolean }) => filterTag.id === tag._id && !filterTag.negative);
                
                return (
                  <div
                    key={tag._id}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hasNewFilters) {
                        const tagId = `${activeTagMode || 'inclusive'}-${tag.name}`;
                        filterDispatch({
                          type: 'TOGGLE_TAG_MODE',
                          tagId,
                          value: tag.name,
                          category: undefined
                        });
                      } else {
                        handleTagClick(tag._id);
                      }
                    }}
                  >
                    {hasNewFilters && isActiveInNewSystem ? (
                      <SimpleTagChip
                        tag={tag.name}
                        mode={activeTagMode!}
                        onClick={() => {
                          const tagId = `${activeTagMode}-${tag.name}`;
                          filterDispatch({
                            type: 'TOGGLE_TAG_MODE',
                            tagId,
                            value: tag.name,
                            category: undefined
                          });
                        }}
                        size="sm"
                      />
                    ) : (
                      <TagChip
                        tag={tag}
                        onClick={(tagId) => {
                          if (hasNewFilters) {
                            filterDispatch({
                              type: 'TOGGLE_TAG_MODE',
                              tagId: `inclusive-${tag.name}`,
                              value: tag.name,
                              category: undefined
                            });
                          } else {
                            handleTagClick(tagId);
                          }
                        }}
                        variant="default"
                        size="sm"
                        isActive={isActiveInLegacy}
                      />
                    )}
                  </div>
                );
              })}
              {ref.tags.length > 3 && (
                <span className="badge badge-ghost text-xs text-gray-600">
                  +{ref.tags.length - 3} more
                </span>
              )}
            </div>
          )}

          {/* URL + Brand */}
          {ref.sourceURL && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 truncate">
              {ref.app?.brandData?.logos?.[0] && (
                <img
                  src={ref.app.brandData.logos[0].url}
                  alt=""
                  className="h-4 w-4 object-contain flex-shrink-0"
                />
              )}
              {getHostnameFromUrl(ref.sourceURL)}
            </div>
          )}
          {ref.app?.brandData?.colors && ref.app.brandData.colors.length > 0 && (
            <div className="flex gap-1 mt-1">
              {ref.app.brandData.colors.slice(0, 5).map((color, i) => (
                <div
                  key={i}
                  className="w-3 h-3 rounded-full border border-base-300"
                  style={{ backgroundColor: color.hex }}
                  title={color.usage || color.hex}
                />
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="card-actions justify-end mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-base-300 min-h-[40px] sm:min-h-[44px] flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
            type="button"
            aria-label={`View ${ref.title}`}
            className="btn btn-sm btn-outline text-xs sm:text-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 sm:h-4 w-3 sm:w-4 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="hidden sm:inline">View</span>
          </button>
          {onAIPrompt && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAIPrompt(ref);
              }}
              type="button"
              aria-label={`Generate AI prompt for ${ref.title}`}
              className="btn btn-sm btn-secondary text-xs sm:text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 sm:h-4 w-3 sm:w-4 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span className="hidden sm:inline">AI</span>
            </button>
          )}
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(ref);
              }}
              type="button"
              aria-label={`Edit ${ref.title}`}
              className="btn btn-sm btn-info text-xs sm:text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 sm:h-4 w-3 sm:w-4 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span className="hidden sm:inline">Edit</span>
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(ref);
              }}
              className="btn btn-error btn-sm text-xs sm:text-sm"
              title="Delete reference"
            >
              <span className="hidden sm:inline">Delete</span>
              <span className="sm:hidden">Del</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
} 